import { CustomerDealSummary } from './CustomerOrderSummary';
import type { CustomerQuote } from './CustomerQuote';
import { getFixingIndicesList, type FixingDetails } from './FixingDetails';
import type {
  CustomerExecutionStrategyEnum,
  CustomerWorkflowEnum,
  CxlRejReasonEnum,
  DecisionStatusEnum,
  ExecTypeEnum,
  ICustomerOrderSummary,
  IMarketViewRequest4146Markets,
  IStrategyParameters,
  OrdRejReasonEnum,
  OrdStatusEnum,
  OrdTypeEnum,
  PricingModeEnum,
  SideEnum,
  TimeInForceEnum,
} from './types';

export const isCustomerOrder = (entity: any): entity is CustomerOrder => {
  return entity instanceof CustomerOrder;
};

/** org.trader.admin users are allowed to force-cancel customer orders that are S2F and in status PendingFix. */
export function canTraderAdminForceCancelCustomerOrder(orderOrQuote: CustomerOrder | CustomerQuote) {
  const status = orderOrQuote instanceof CustomerOrder ? orderOrQuote.OrdStatus : orderOrQuote.QuoteStatus;
  return status === 'PendingFix' && orderOrQuote.PricingMode === 'SpreadToFixing';
}

export interface PricingParameters {
  AcceptPriceLeniency: string;
  AcceptTimeLeniency: string;
  EnableSyntheticCounterCurrency: boolean;
  MinMarketDataThrottle: string;
  OrderPriceImprovementRatio: string;
  PriceImprovementRatio: string;
  PricesTimeout: string;
  QuoteTTL: string;
  RFQTTL: string;
  // Either Spread or OfferSpread/BidSpread exists as a Pricing Parameter
  Spread?: string;
  OfferSpread?: string;
  BidSpread?: string;
  PricingMarkets?: IMarketViewRequest4146Markets[];
}

export class CustomerOrder {
  static readonly rowID = 'OrderID';

  AggressorSide?: SideEnum;
  AmountCurrency!: string;
  AvgPx?: string;
  AvgPxAllIn?: string;
  ClOrdID!: string;
  Counterparty?: string;
  CumAmt!: string;

  CumQty!: string;
  /**
   * The currency of the quantity the order was placed with.
   * Note: a currency can be excluded from an order. For example if the order is placed in number of contracts.
   */
  Currency?: string;

  CustomerUser?: string;
  ExecID!: string;
  LeavesQty!: string;
  MarketAccount!: string;
  OrdStatus!: OrdStatusEnum;
  OrdType!: OrdTypeEnum;
  OrderID!: string;
  OrderQty!: string;
  Price?: string;
  PricingParameters?: PricingParameters;
  QuoteID?: string;
  RFQID?: string;
  Side!: SideEnum;
  SubmitTime!: string;
  /** Symbol is undefined for Compound customer orders */
  Symbol?: string;
  TimeInForce?: TimeInForceEnum;
  Timestamp?: string;
  TransactTime?: string;
  User?: string;
  CumFee?: string;
  DecisionStatus!: DecisionStatusEnum;
  ExecType?: ExecTypeEnum;
  FeeCurrency?: string;
  Strategy?: string;
  Text?: string;
  InternalText?: string;
  OrigText?: string;
  StartTime?: string;
  EndTime?: string;
  OrdRejReason?: OrdRejReasonEnum;
  OrigClOrdID?: string;
  CxlRejReason?: CxlRejReasonEnum;
  Parameters?: Partial<IStrategyParameters>;
  summary?: CustomerDealSummary;
  SubAccount?: string;
  Comments?: string;
  Group?: string;
  ExpectedFillPrice?: string;
  ExpectedFillQty?: string;
  AdminFillPrice?: string;
  PricingMode?: PricingModeEnum;
  PricingReference?: string | undefined;

  LegSummary?: {
    LegIndex: number;
    Symbol: string;
    // More to be added here as it is needed
    FixingDetails?: {
      Index?: string;
      Fixing?: string;
    };
  }[];

  FixingDetails?: FixingDetails;
  /** An array of all the fixing indices referenced on this entity */
  fixingIndices: string[] | undefined;

  TargetSpread?: ICustomerOrderSummary['TargetSpread'];
  ActualSpread?: ICustomerOrderSummary['ActualSpread'];
  PerCurrencyPnL?: ICustomerOrderSummary['PerCurrencyPnL'];
  PerCurrencyFees?: ICustomerOrderSummary['PerCurrencyFees'];
  SalesCommission?: ICustomerOrderSummary['SalesCommission'];
  basePnL?: string;
  basePnLCurrency?: string;
  counterPnL?: string;
  counterPnLCurrency?: string;
  ExecutionStrategy?: CustomerExecutionStrategyEnum;
  Workflow?: CustomerWorkflowEnum;

  /**
   * CustomerOrder that are compound do not have a top-level Symbol, instead they have LegSummary[].Symbol
   * This getter exposes whichever of these two exists
   */
  get symbolOrSymbols(): string | string[] | undefined {
    return this.Symbol ?? this.legSymbols;
  }

  /** If there are LegSummary records, will return the Symbols found in those records */
  get legSymbols(): string[] | undefined {
    return this.LegSummary?.map(ls => ls.Symbol);
  }

  /**
   * Monitoring blotter displays CustomerOrder objects originating from CUSTOMER_ORDERS subscription, however
   * we would like the rows to contain customer summary specific properties which are extracted from CUSTOMER_ORDER_SUMMARY subscription
   */
  public enrichOrderWithCustomerSummary?(customerOrderSummary?: ICustomerOrderSummary): void {
    if (!customerOrderSummary) {
      return;
    }
    this.TargetSpread = customerOrderSummary.TargetSpread;
    this.ActualSpread = customerOrderSummary.ActualSpread;
    this.PerCurrencyPnL = customerOrderSummary.PerCurrencyPnL;
    this.PerCurrencyFees = customerOrderSummary.PerCurrencyFees;
    this.SalesCommission = customerOrderSummary.SalesCommission;
    const basePnL = getPerCurrencyPnL(customerOrderSummary.PerCurrencyPnL, customerOrderSummary.Currency);
    this.basePnL = basePnL?.Amount;
    this.basePnLCurrency = basePnL?.Currency;
    const counterPnL = getPerCurrencyPnL(customerOrderSummary.PerCurrencyPnL, this.AmountCurrency);
    this.counterPnL = counterPnL?.Amount;
    this.counterPnLCurrency = counterPnL?.Currency;
    this.summary = new CustomerDealSummary(customerOrderSummary);
  }

  constructor(data: ICustomerOrder | CustomerOrder) {
    Object.assign(this, data);
    this.fixingIndices = getFixingIndicesList(this.FixingDetails);
  }
}

type CustomerOrderGetters = keyof Pick<CustomerOrder, 'symbolOrSymbols' | 'legSymbols' | 'fixingIndices'>;

export type ICustomerOrder = Omit<CustomerOrder, CustomerOrderGetters>;

function getPerCurrencyPnL(
  perCurrencyPnL: ICustomerOrderSummary['PerCurrencyPnL'],
  currency: string | undefined
): ICustomerOrderSummary['PerCurrencyPnL'][number] | undefined {
  return currency ? perCurrencyPnL?.find(pnl => pnl.Currency === currency) : undefined;
}
